import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { RegexPatterns } from "./../../../../../shared/regex/regex-patterns";
import { ViewStateService } from "../../../../../shared/services/view-state.service";
import { CVPurchaseService } from "./cv-purchase.service";
import { DirectoryService } from "../../../../shared/services/directory.service";
import { NotificationsService } from "./../../../../../shared/services/notifications.service";
import { CVDetails, CVPurchasePayment, CVRequest } from "./cv-purchase.model";
import { RecaptchaComponent } from "ng-recaptcha";
import { debounceTime, takeUntil } from "rxjs/operators";
import { environment } from "environments/environment";
import { PurchaseService } from "../../../../../purchase/shared/services/purchase.service";

enum CardTypes {
  Amex,
  Discover,
  MasterCard,
  Visa,
}

declare let Accept: any;

@Component({
  selector: "app-cv-purchase",
  templateUrl: "./cv-purchase.component.html",
})
export class CVPurchaseComponent implements OnInit, OnDestroy {
  paymentForm: UntypedFormGroup;
  formLevelErrors: string[] = [];
  public readonly siteKey = "6LfyV20aAAAAADgafr-4CKTNvUpqd3NEsZ4nwmfB";

  public captchaIsLoaded = false;
  public captchaSuccess = false;
  public captchaIsExpired = false;
  public captchaResponse: string | null = null;
  @ViewChild('captchaElem') captchaElem?: RecaptchaComponent;

  handleSuccess(token: string | null): void {
    this.captchaSuccess = !!token;
    this.captchaResponse = token;
    this.captchaIsExpired = !token;
    this.cdr.detectChanges();
  }

  private examFee: number;
  private examId: number;
  private quantity: number;
  private destroy$ = new Subject<void>();

  cardTypes = CardTypes;
  ccType: CardTypes;
  amount: number;
  paymentSent = false;
  schoolId = 0;
  firstName: string;
  lastName: string;
  emailIn: string;
  personId: string;
  practitionerName: string;
  get email(): string {
    return this.formValues.email;
  }

  @Output() formSubmitted: EventEmitter<any> = new EventEmitter();

  constructor(
    private cdr: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
    private cvPurchase: CVPurchaseService,
    private purchaseService: PurchaseService,
    private directoryService: DirectoryService,
    private viewState: ViewStateService,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private notifications: NotificationsService
  ) { }

  ngOnInit() {
    this.viewState.startLoading();
    this.buildForm();
    if (this.activeRoute.snapshot.paramMap.get("personId")) {
      this.setupFormDefaults();
      this.personId = this.activeRoute.snapshot.paramMap.get("personId");
      this.practitionerName =
        this.activeRoute.snapshot.paramMap.get("practitionerName");
    }
    this.setupFormListeners();

    this.purchaseService
      .loadAcceptJs()
      .then(() => {
        console.log("Accept.js is ready");
      })
      .catch((err) => {
        console.error(err);
      });

    this.paymentForm.controls["cardNumber"].valueChanges
      .pipe(debounceTime(500))
      .subscribe((v: string) => {
        if (v.match("^4[0-9]{12}(?:[0-9]{3})?$")) {
          this.ccType = CardTypes.Visa;
        } else if (
          v.match(
            "^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$"
          )
        ) {
          this.ccType = CardTypes.MasterCard;
        } else if (v.match("^3[47][0-9]{13}$")) {
          this.ccType = CardTypes.Amex;
        } else if (v.match("^6(?:011|5[0-9]{2})[0-9]{12}$")) {
          this.ccType = CardTypes.Discover;
        } else {
          this.ccType = null;
        }
      });

    this.viewState.stopLoading();
  }

  setupFormDefaults() {
    this.paymentForm.setValue(this.formValues);
  }

  onSubmit(updatedFormValues: any) {
    if (!this.captchaSuccess) {
      alert("Are you not a robot?");
      return;
    }

    if (!this.captchaResponse) {
      alert("Captcha load failed. Please refresh screen and try again");
      return;
    }

    this.viewState.startLoading();
    this.formValues = updatedFormValues;
    this.validateForm();
    if (this.paymentForm.valid) { this.submitPaymentInitial(); }
  }

  onCancel(event: Event) {
    event.preventDefault();

    this.paymentForm.reset();
  }

  private setupFormListeners() {
    // this.paymentForm.controls["serviceId"].valueChanges
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe((id) => {
    //     let exam = this.examList.find((e) => e.serviceId == id);
    //     this.examFee = exam ? exam.activeRate : 0;
    //     this.examId = exam ? exam.examId : 0;
    //     this.updateFee();
    //   });

    this.paymentForm.controls["quantity"].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((qty) => {
        this.quantity = qty;
        this.updateFee();
      });

    this.paymentForm.controls["cardNumber"].valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((v: string) => {
        if (v.match("^4[0-9]{12}(?:[0-9]{3})?$")) { this.ccType = CardTypes.Visa; }
        else if (
          v.match(
            "^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$"
          )
        ) { this.ccType = CardTypes.MasterCard; }
        else if (v.match("^3[47][0-9]{13}$")) { this.ccType = CardTypes.Amex; }
        else if (v.match("^6(?:011|5[0-9]{2})[0-9]{12}$")) { this.ccType = CardTypes.Discover; }
        else { this.ccType = null; }
      });
  }

  private submitPaymentInitial() {
    const authData = {
      clientKey: environment.clientKey,
      apiLoginID: environment.apiLogin,
    };

    const cardData = {
      cardNumber: this.formValues.cardNumber,
      month: this.formValues.expirationDate.substring(0, 2),
      year: 20 + this.formValues.expirationDate.substring(2),
      cardCode: this.formValues.ccv,
    };

    const secureData = {
      authData,
      cardData,
    };
    //console.log(authData);
    //console.log(cardData);

    Accept.dispatchData(secureData, this.responseHandler.bind(this));
  }

  responseHandler(response: any) {
    if (response.messages.resultCode === "Error") {
      console.error(
        "Error creating payment nonce/token:",
        response.messages.message
      );
      console.log(response.messages);
      let err = response.messages.message[0].text;
      if (!err || err == "") { err = "Please review credit card information and resubmit"; }
      this.notifications.error(
        "Payment Failed",
        err
      );
      this.captchaElem?.reset();
      this.handleReset();
      this.viewState.stopLoading();
      return;
    }

    const opaqueData = response.opaqueData;
    const paymentData = this.mapFormToCVPayment(opaqueData);
    this.cvPurchase.purchaseCV(paymentData).subscribe(
      (res) => {
        this.paymentSent = true;
        this.viewState.stopLoading();
      },
      (error) => {
        this.viewState.stopLoading();
        console.log(error);
        this.captchaElem?.reset();
        this.handleReset();
        this.notifications.error(
          "Payment Failed",
          "Your payment could not be approved."
        );
      }
    );
  }

  private updateFee() {
    if (!this.examFee || !this.quantity) {
      this.amount = null;
      return;
    }
    this.amount = this.examFee * this.quantity;
  }

  //   private submitPayment() {
  //     const paymentData = this.mapFormToCVPayment();
  //     this.formLevelErrors = [];

  //     this.cvPurchase
  //       .purchaseCV(paymentData)
  //       .pipe(takeUntil(this.destroy$))
  //       .subscribe(
  //         (res) => {
  //           this.paymentSent = true;
  //           this.viewState.stopLoading();
  //         },
  //         (error) => {
  //           this.viewState.stopLoading();
  //           this.captchaElem.resetCaptcha();
  //           if (!error.error.errorCode) {
  //             // Only map server errors if the error coming back matches
  //             this.mapServerErrorsToForm(error.error);
  //           } else {
  //             this.formLevelErrors.push(
  //               error.error.errorCode + " " + error.error.errorMessage
  //             );
  //           }
  //           this.notifications.error(
  //             "Payment Failed",
  //             "Your payment could not be approved."
  //           );
  //         }
  //       );
  //   }

  private mapFormToCVPayment(opaqueData): CVPurchasePayment {
    const details = [];
    const paymentDetails = new CVDetails();
    const cvRequest = new CVRequest();
    cvRequest.cvRequestFor = "E";
    cvRequest.email = this.formValues.email;
    paymentDetails.serviceCodeId = 108;
    paymentDetails.cost = 5;
    paymentDetails.quantity = 1; //parseInt(this.formValues.quantity);
    paymentDetails.cvRequest = cvRequest;
    details.push(paymentDetails);

    const payment = new CVPurchasePayment();
    payment.personId = this.personId;
    payment.address = this.formValues.address;
    payment.cardCode = ""; //this.formValues.ccv;
    payment.cardNumber = ""; //this.formValues.cardNumber;
    payment.city = this.formValues.city;
    payment.companyName = this.formValues.company;
    payment.details = details;
    payment.email = this.formValues.email;
    payment.expirationDate = ""; //this.formValues.expirationDate.replace("/", "");
    payment.firstName = this.formValues.firstName;
    payment.lastName = this.formValues.lastName;
    payment.payerName =
      this.formValues.firstName + " " + this.formValues.lastName;
    payment.paymentType = "CC";

    payment.dataDescriptor = opaqueData.dataDescriptor;
    payment.dataValue = opaqueData.dataValue;

    payment.state = this.formValues.state;
    payment.totalCost = 5;
    payment.zip = this.formValues.zip;
    payment.recaptcha = this.captchaResponse;
    return payment;
  }

  private buildForm(): void {
    this.paymentForm = this.formBuilder.group({
      recaptcha: ["", Validators.required],
      quantity: [this.formValues.quantity, []],
      email: [this.formValues.email, [Validators.required, Validators.email]],
      company: [this.formValues.company],
      firstName: [this.formValues.firstName, [Validators.required]],
      lastName: [this.formValues.lastName, [Validators.required]],
      cardNumber: [
        this.formValues.cardNumber,
        [
          Validators.required,
          Validators.minLength(15),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      expirationDate: [this.formValues.expirationDate, [Validators.required]],
      ccv: [
        this.formValues.ccv,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      address: [this.formValues.address, Validators.required],
      zip: [this.formValues.zip, [Validators.required]],
      city: [this.formValues.city, [Validators.required]],
      state: [this.formValues.state, [Validators.required]],
      schoolId: [this.formValues.schoolId, []],
    });
  }

  private validateForm() {
    this.paymentForm.updateValueAndValidity;

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.paymentForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) {
      this.notifications.error(
        "Fields Invalid",
        "Please review the form and resubmit."
      );
      this.viewState.stopLoading();
    }
  }

  private formValues = {
    recaptcha: "",
    quantity: "",
    email: "",
    company: "",
    firstName: "",
    lastName: "",
    cardNumber: "",
    expirationDate: "",
    ccv: "",
    address: "",
    zip: "",
    city: "",
    state: "",
    schoolId: null,
  };

  formErrors = {
    quantity: "",
    email: "",
    company: "",
    firstName: "",
    lastName: "",
    cardNumber: "",
    expirationDate: "",
    ccv: "",
    address: "",
    zip: "",
    city: "",
    state: "",
  };

  validationMessages = {
    serviceId: {
      required: "Exam is required.",
    },
    quantity: {
      required: "Quantity is required.",
    },
    email: {
      required: "Email is required.",
      email: "Email must be valid.",
    },
    company: {},
    firstName: {
      required: "First Name is required.",
    },
    lastName: {
      required: "Last Name is required.",
    },
    cardNumber: {
      required: "Card Number is required.",
      minlength: "Card Number is invalid.",
      pattern: "Card Number is invalid.",
    },
    expirationDate: {
      required: "Expiration is required.",
    },
    ccv: {
      required: "CCV is required.",
      minlength: "CCV is invalid.",
      pattern: "CCV is invalid.",
    },
    address: {
      required: "Address is required.",
    },
    city: {
      required: "City is required.",
    },
    state: {
      required: "State is required.",
    },
    zip: {
      required: "Postal Code is required.",
      mask: "Postal Code must be five digits."
    },
  };
  // handleReset(): void {
  //   this.captchaSuccess = false;
  //   this.captchaResponse = undefined;
  //   this.captchaIsExpired = false;
  //   this.cdr.detectChanges();
  // }

  // handleSuccess(captchaResponse: string): void {
  //   this.captchaSuccess = true;
  //   this.captchaResponse = captchaResponse;
  //   this.captchaIsExpired = false;
  //   this.cdr.detectChanges();
  // }

  // handleLoad(): void {
  //   this.captchaIsLoaded = true;
  //   this.captchaIsExpired = false;
  //   this.cdr.detectChanges();
  // }

  // handleExpire(): void {
  //   this.captchaSuccess = false;
  //   this.captchaIsExpired = true;
  //   this.cdr.detectChanges();
  // }
  // getCurrentResponse(): void {
  //   const currentResponse = this.captchaElem.getCurrentResponse();
  //   if (!currentResponse) {
  //     alert("There is no current response - have you submitted captcha?");
  //   } else {
  //     alert(currentResponse);
  //   }
  // }

  // getResponse(): void {
  //   const response = this.captchaElem.getResponse();
  //   if (!response) {
  //     alert("There is no response - have you submitted captcha?");
  //   } else {
  //     alert(response);
  //   }
  // }

  // reload(): void {
  //   this.captchaElem.reloadCaptcha();
  // }

  // getCaptchaId(): void {
  //   alert(this.captchaElem.getCaptchaId());
  // }

  // reset(): void {
  //   this.captchaElem.resetCaptcha();
  // }

  private mapServerErrorsToForm(errorObj: any) {
    const errors = errorObj;
    errors.map((error) => {
      this.formErrors[error.fieldName] = "";
      const control = this.paymentForm.get(error.fieldName);
      this.formErrors[error.fieldName] += error.message + " ";
      if (!control) {
        this.formLevelErrors.push(error.message);
      }
    });
    this.viewState.stopLoading();
  }

  handleReset(): void {
    this.captchaSuccess = false;
    this.captchaResponse = null;
    this.captchaIsExpired = false;
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
